import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Link,
  Paper
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as WebsiteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  AttachMoney as RevenueIcon,
  Code as TechIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  GitHub as GitHubIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

/**
 * DiffbotProfile Component - Displays rich organization data from Diffbot API
 * 
 * @param {Object} props - Component props
 * @param {Object} props.diffbotData - Diffbot organization data
 * @param {Boolean} props.compact - Whether to show compact view
 */
const DiffbotProfile = ({ diffbotData, compact = false }) => {
  if (!diffbotData) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No Diffbot organization data available. 
            Refresh the organization profile to fetch detailed company information.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Handle nested entity structure from Diffbot API
  const entityData = diffbotData.entity || diffbotData;
  console.log('🔧 DiffbotProfile received data:', entityData);

  const formatRevenue = (revenue) => {
    if (!revenue?.value) return 'Not available';
    return `$${(revenue.value / 1000000).toFixed(1)}M ${revenue.currency}`;
  };

  const formatDate = (foundingDate) => {
    if (!foundingDate?.str) return 'Not available';
    return new Date(foundingDate.timestamp).getFullYear();
  };

  const getSocialIcon = (uri) => {
    if (uri.includes('linkedin')) return <LinkedInIcon fontSize="small" />;
    if (uri.includes('twitter')) return <TwitterIcon fontSize="small" />;
    if (uri.includes('facebook')) return <FacebookIcon fontSize="small" />;
    if (uri.includes('github')) return <GitHubIcon fontSize="small" />;
    return <WebsiteIcon fontSize="small" />;
  };

  const topTechnologies = entityData.technographics?.slice(0, 8) || [];
  const employeeCategories = entityData.employeeCategories?.slice(0, 6) || [];

  if (compact) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            {entityData.logo && (
              <Avatar 
                src={entityData.logo} 
                alt={entityData.name}
                sx={{ width: 56, height: 56 }}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                {entityData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {entityData.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<BusinessIcon />} 
                  label={entityData.sicClassification?.[0]?.name || 'Technology'} 
                  size="small" 
                />
                <Chip 
                  icon={<PeopleIcon />} 
                  label={`${entityData.nbEmployees} employees`} 
                  size="small" 
                />
                <Chip 
                  icon={<RevenueIcon />} 
                  label={formatRevenue(entityData.revenue)} 
                  size="small" 
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          {entityData.logo && (
            <Avatar 
              src={entityData.logo} 
              alt={entityData.name}
              sx={{ width: 80, height: 80 }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" gutterBottom>
              {entityData.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {entityData.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {entityData.categories?.slice(0, 3).map((category, index) => (
                <Chip 
                  key={index}
                  label={category.name} 
                  size="small" 
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Company Overview */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon /> Company Overview
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CalendarIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Founded" 
                  secondary={formatDate(entityData.foundingDate)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Employees" 
                  secondary={`${entityData.nbEmployees} (${entityData.nbEmployeesMin}-${entityData.nbEmployeesMax})`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><RevenueIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Revenue" 
                  secondary={formatRevenue(entityData.revenue)}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon fontSize="small" /></ListItemIcon>
                <ListItemText 
                  primary="Location" 
                  secondary={entityData.location?.address || entityData.location || 'Not available'}
                />
              </ListItem>
              {entityData.ceo && (
                <ListItem>
                  <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="CEO" 
                    secondary={entityData.ceo.name}
                  />
                </ListItem>
              )}
            </List>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon /> Contact Information
            </Typography>
            <List dense>
              {entityData.homepageUri && (
                <ListItem>
                  <ListItemIcon><WebsiteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Website" 
                    secondary={
                      <Link href={`https://${entityData.homepageUri}`} target="_blank" rel="noopener">
                        {entityData.homepageUri}
                      </Link>
                    }
                  />
                </ListItem>
              )}
              {entityData.emailAddresses?.slice(0, 2).map((email, index) => (
                <ListItem key={index}>
                  <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary={email.type === 'PROFESSIONAL' ? 'Professional Email' : 'Email'} 
                    secondary={
                      <Link href={`mailto:${email.contactString}`}>
                        {email.contactString}
                      </Link>
                    }
                  />
                </ListItem>
              ))}
              {entityData.phoneNumbers?.slice(0, 2).map((phone, index) => (
                <ListItem key={index}>
                  <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                  <ListItemText 
                    primary="Phone" 
                    secondary={phone.string}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Social Profiles */}
          {(entityData.linkedInUri || entityData.twitterUri || entityData.facebookUri || entityData.githubUri) && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Social Profiles
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {entityData.linkedInUri && (
                  <Chip 
                    icon={getSocialIcon(entityData.linkedInUri)}
                    label="LinkedIn" 
                    component="a"
                    href={`https://${entityData.linkedInUri}`}
                    target="_blank"
                    clickable
                    size="small"
                  />
                )}
                {entityData.twitterUri && (
                  <Chip 
                    icon={getSocialIcon(entityData.twitterUri)}
                    label="Twitter" 
                    component="a"
                    href={`https://${entityData.twitterUri}`}
                    target="_blank"
                    clickable
                    size="small"
                  />
                )}
                {entityData.facebookUri && (
                  <Chip 
                    icon={getSocialIcon(entityData.facebookUri)}
                    label="Facebook" 
                    component="a"
                    href={`https://${entityData.facebookUri}`}
                    target="_blank"
                    clickable
                    size="small"
                  />
                )}
                {entityData.githubUri && (
                  <Chip 
                    icon={getSocialIcon(entityData.githubUri)}
                    label="GitHub" 
                    component="a"
                    href={`https://${entityData.githubUri}`}
                    target="_blank"
                    clickable
                    size="small"
                  />
                )}
              </Box>
            </Grid>
          )}

          {/* Employee Categories */}
          {employeeCategories.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Team Composition
              </Typography>
              <List dense>
                {employeeCategories.map((category, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={category.category} 
                      secondary={`${category.nbEmployees} employees`}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}

          {/* Technology Stack */}
          {topTechnologies.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TechIcon /> Technology Stack ({entityData.technographics?.length} technologies)
              </Typography>
              
              {/* Group technologies by category */}
              {(() => {
                const techByCategory = {};
                entityData.technographics?.forEach(tech => {
                  tech.categories?.forEach(category => {
                    if (!techByCategory[category]) techByCategory[category] = [];
                    techByCategory[category].push(tech.technology.name);
                  });
                });
                
                return Object.keys(techByCategory).slice(0, 6).map((category, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {techByCategory[category].slice(0, 8).map((tech, techIndex) => (
                        <Chip 
                          key={techIndex}
                          label={tech} 
                          size="small" 
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                      {techByCategory[category].length > 8 && (
                        <Chip 
                          label={`+${techByCategory[category].length - 8} more`} 
                          size="small" 
                          variant="filled"
                          color="primary"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                    </Box>
                  </Box>
                ));
              })()}
            </Grid>
          )}

          {/* Business Descriptors & Keywords */}
          {entityData.descriptors && entityData.descriptors.length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon /> Business Focus
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {entityData.descriptors.slice(0, 12).map((descriptor, index) => (
                  <Chip 
                    key={index}
                    label={descriptor} 
                    size="small" 
                    variant="outlined"
                    sx={{ mb: 1, textTransform: 'capitalize' }}
                  />
                ))}
                {entityData.descriptors.length > 12 && (
                  <Chip 
                    label={`+${entityData.descriptors.length - 12} more`} 
                    size="small" 
                    variant="filled"
                    color="secondary"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            </Grid>
          )}

          {/* Key Metrics & Stats */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon /> Key Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                  <Typography variant="h4" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                    {entityData.importance?.toFixed(0) || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'primary.contrastText' }}>
                    Importance Score
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light' }}>
                  <Typography variant="h4" sx={{ color: 'secondary.contrastText', fontWeight: 'bold' }}>
                    {entityData.nbOrigins || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'secondary.contrastText' }}>
                    Data Sources
                  </Typography>
                </Paper>
              </Grid>
              {entityData.nbActiveEmployeeEdges && (
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h4" sx={{ color: 'success.contrastText', fontWeight: 'bold' }}>
                      {entityData.nbActiveEmployeeEdges}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'success.contrastText' }}>
                      Active Employees
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {entityData.hasRemoteInternationalEmployees !== undefined && (
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                    <Typography variant="h6" sx={{ color: 'info.contrastText', fontWeight: 'bold' }}>
                      {entityData.hasRemoteInternationalEmployees ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'info.contrastText' }}>
                      Remote International
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* Company Status & Type */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon /> Company Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip 
                label={entityData.isPublic ? 'Public Company' : 'Private Company'} 
                color={entityData.isPublic ? 'success' : 'default'}
                variant="filled"
              />
              <Chip 
                label={entityData.isNonProfit ? 'Non-Profit' : 'For-Profit'} 
                color={entityData.isNonProfit ? 'info' : 'primary'}
                variant="outlined"
              />
              {entityData.isAcquired !== undefined && (
                <Chip 
                  label={entityData.isAcquired ? 'Acquired' : 'Independent'} 
                  color={entityData.isAcquired ? 'warning' : 'success'}
                  variant="outlined"
                />
              )}
              {entityData.isDissolved !== undefined && !entityData.isDissolved && (
                <Chip 
                  label="Active Business" 
                  color="success"
                  variant="filled"
                />
              )}
            </Box>
            
            {/* Company Types */}
            {entityData.types && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center', fontWeight: 600 }}>
                  Types:
                </Typography>
                {entityData.types.map((type, index) => (
                  <Chip 
                    key={index}
                    label={type} 
                    size="small" 
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Grid>

          {/* Industry Classification */}
          {entityData.sicClassification && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Industry Classification
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {entityData.sicClassification.slice(0, 4).map((classification, index) => (
                  <Chip 
                    key={index}
                    label={`${classification.name} (${classification.code})`} 
                    size="small" 
                    variant={classification.isPrimary ? "filled" : "outlined"}
                    color={classification.isPrimary ? "primary" : "default"}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>

        {entityData.lastUpdated && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Typography variant="caption" color="text.secondary">
              Data provided by Diffbot • Last updated: {new Date(entityData.lastUpdated.seconds * 1000).toLocaleDateString()}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DiffbotProfile;